import React, { ChangeEvent, FormEvent, useCallback, useRef, useState } from 'react';

import { Alert, Button, FormGroup, TextField } from '@mui/material';

import VendorsService from '~/services/VendorsService';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60;

type TotalMap = Record<string, string> | null;

type SuccessMessageProps = {
  total: TotalMap;
  message: string | null;
};

function SuccessMessage({ total, message }: SuccessMessageProps) {
  return (
    <Alert severity="success">
      <strong>Success!</strong>
      <p>{message ? message : 'File upload successful'}</p>
      {total && (
        <>
          <p>Total:</p>
          <ul>
            {Object.entries(total).map(([key, value], index) => (
              <li key={index}>
                Payment Reference {key} {value}
              </li>
            ))}
          </ul>
        </>
      )}
    </Alert>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <Alert severity="error">
      <strong>Oops!</strong>
      <p>File upload failed</p>
      <pre>{message}</pre>
    </Alert>
  );
}

export default function UploadWexVccStatements() {
  const [file, setFile] = useState<File | null>(null);
  const [successMessage, setSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [total, setTotal] = useState<TotalMap>(null);
  const [message, setMessage] = useState<string | null>(null);
  const attemptsRef = useRef(0);

  const showError = useCallback((msg: string) => {
    setSuccessMessage(false);
    setShowErrorMessage(true);
    setErrorMessage(msg);
    setFile(null);
    setTotal(null);
    setMessage(null);
  }, []);

  const pollStatus = useCallback(
    (jobId: string) => {
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        showError(`Upload status check timed out after ${MAX_POLL_ATTEMPTS} attempts. Check the job status manually.`);
        return;
      }
      attemptsRef.current += 1;

      setTimeout(() => {
        VendorsService.checkUploadJobStatus(jobId)
          .then((result) => {
            const rowsProcessed = result.rows_processed || 0;

            if (result.job_state !== 'completed' && result.job_state !== 'failed') {
              setSuccessMessage(true);
              setMessage(`File processing... ${rowsProcessed} rows processed.`);
              pollStatus(jobId);
              return;
            }

            if (result.status === 'success') {
              setSuccessMessage(true);
              setTotal(result.result);
              setMessage(
                rowsProcessed > 0
                  ? 'File uploaded successfully.'
                  : 'File uploaded successfully but 0 rows processed. Please check file format and/or column names.',
              );
              return;
            }

            showError(result.result);
          })
          .catch((err) => {
            showError(`Failed to check upload status: ${err?.message ?? String(err)}`);
          });
      }, POLL_INTERVAL_MS);
    },
    [showError],
  );

  const onFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!file) {
      showError('Missing file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('wex-vcc-payments', file);
      const result = await VendorsService.uploadWexVccStatements(formData);

      setSuccessMessage(true);
      setShowErrorMessage(false);
      setErrorMessage('');
      setFile(null);
      setTotal(null);
      setMessage(result.message);
      attemptsRef.current = 0;
      pollStatus(result.jobId);
    } catch (err) {
      showError(err?.message ?? String(err));
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
  };

  return (
    <div>
      <p>WEX VCC statements will not be reflected in any reports until the following day.</p>

      {successMessage && <SuccessMessage message={message} total={total} />}
      {showErrorMessage && <ErrorMessage message={errorMessage} />}

      <form onSubmit={onFormSubmit}>
        <FormGroup sx={{ marginTop: '20px' }}>
          <TextField
            label="File Upload"
            type="file"
            name="file"
            onChange={onChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              accept:
                '.csv, .xls, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
            }}
            fullWidth
          />
        </FormGroup>
        <Button sx={{ marginTop: '10px' }} type="submit" variant="contained">
          Upload statement
        </Button>
      </form>
    </div>
  );
}
